// ============================================================================
// REQUIRED-FIELDS MANIFEST — single source of truth
// ============================================================================
//
// This manifest is THE definition of "what a complete, in-depth profile must
// contain." It is consumed by three things:
//
//   1. The signup flows (RequestJoin + the authed wizard) — to validate that
//      new submissions collect everything (via the shared Step6 schema and the
//      edge function's server-side checks).
//   2. The backfill prompt — to detect which required fields an EXISTING
//      (profile_completed = true) member is missing, and to render a focused
//      fill-in of ONLY those fields.
//   3. The edge function — mirrors the `required` set for server-side rejection.
//
// HOW TO ADD A FUTURE REQUIRED FIELD (the whole point of this design):
//   a) Write an additive migration adding the column/table.
//   b) Add ONE entry to MANIFEST below: where it lives, how to read it from a
//      loaded snapshot, and how to render its input.
//   c) Done. The backfill prompt picks it up automatically because "missing" is
//      DERIVED FROM ACTUAL DATA (the `isMissing` predicate), never from a
//      version counter. Anyone whose data lacks it gets prompted on next login.
//
// There is intentionally no schema-version column. A member is "missing" a
// field iff `isMissing(snapshot)` returns true for it — pure function of data.

import type { SupabaseClient } from "@supabase/supabase-js";

// ----------------------------------------------------------------------------
// Where a required field physically lives. Drives how the backfill loads &
// persists it.
// ----------------------------------------------------------------------------
export type FieldLocation =
  | "profiles" // a column on public.profiles
  | "contact_info" // a column on public.contact_info
  | "profile_references"; // rows in public.profile_references (special-cased)

// A single reference as collected/stored.
export interface ProfileReference {
  name: string;
  relationship: string;
  phone?: string | null;
  email?: string | null;
}

// The data snapshot the manifest reasons over. Loaded once per check.
export interface ProfileSnapshot {
  profile: Record<string, unknown> | null;
  contact: Record<string, unknown> | null;
  references: ProfileReference[];
}

// How the backfill UI should render the input for a missing field.
export type FieldInputKind =
  | "text"
  | "textarea"
  | "tel"
  | "references"
  | "photo"; // image upload — value stored is a storage path, not a URL

export interface RequiredFieldSpec {
  /** Stable key — also the form field name in the focused fill-in. */
  key: string;
  /** Human label shown in the fill-in. */
  label: string;
  /** Short helper text fitting the committee positioning. */
  helper?: string;
  /** Where it lives — drives load + persist. */
  location: FieldLocation;
  /** Column name when location is profiles/contact_info. */
  column?: string;
  /** How to render the input in the backfill dialog. */
  input: FieldInputKind;
  /**
   * The ONLY definition of "missing." Pure function of the loaded snapshot.
   * Returns true when this required field is absent/blank for the member.
   */
  isMissing: (snap: ProfileSnapshot) => boolean;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
const isBlank = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/** A reference is valid iff it has a name, a relationship, and at least one of phone/email. */
export function isValidReference(r: Partial<ProfileReference> | null | undefined): boolean {
  if (!r) return false;
  if (isBlank(r.name) || isBlank(r.relationship)) return false;
  return !isBlank(r.phone) || !isBlank(r.email);
}

export const REQUIRED_REFERENCE_COUNT = 2;

/** Manifest key for the profile photo — referenced by the signup wizard. */
export const PROFILE_PHOTO_FIELD_KEY = "profile_picture_url";

// ----------------------------------------------------------------------------
// THE MANIFEST
// ----------------------------------------------------------------------------
export const REQUIRED_FIELDS: RequiredFieldSpec[] = [
  {
    key: "phone",
    label: "Phone number",
    helper:
      "Your matchmaker may call to talk things through — the traditional way.",
    location: "contact_info",
    column: "phone",
    input: "tel",
    isMissing: (s) => isBlank(s.contact?.phone),
  },
  {
    key: PROFILE_PHOTO_FIELD_KEY,
    label: "Profile photo",
    helper:
      "A clear photo of you. Matchmakers introduce people, not profiles — a face makes that possible.",
    location: "profiles",
    column: "profile_picture_url",
    input: "photo",
    isMissing: (s) => isBlank(s.profile?.profile_picture_url),
  },
  {
    key: "community_connection",
    label: "Your connection to the community",
    helper:
      "How are you connected to our community? (e.g. which masjid you attend, who you know, how you found us.) This helps our matchmakers — fellow community members — place you.",
    location: "profiles",
    column: "community_connection",
    input: "textarea",
    isMissing: (s) => isBlank(s.profile?.community_connection),
  },
  {
    key: "references",
    label: "Two references",
    helper:
      "Two people who know you well — so we can ask around about you the traditional way. For each: their name, how they know you, and a phone or email.",
    location: "profile_references",
    input: "references",
    isMissing: (s) =>
      s.references.filter(isValidReference).length < REQUIRED_REFERENCE_COUNT,
  },
];

// ----------------------------------------------------------------------------
// Loading & checking
// ----------------------------------------------------------------------------

/** Looks up a single manifest entry by key. */
export function getRequiredFieldSpec(key: string): RequiredFieldSpec | undefined {
  return REQUIRED_FIELDS.find((f) => f.key === key);
}

/**
 * Checks ONE manifest entry against a partial snapshot. For UIs that hold a
 * field's value in local state (e.g. the signup wizard, which has the photo in
 * hand before any row exists) and must not restate "required" themselves —
 * the predicate still comes from the manifest.
 */
export function isRequiredFieldMissing(
  key: string,
  partial: Partial<ProfileSnapshot>,
): boolean {
  const spec = getRequiredFieldSpec(key);
  if (!spec) return false;
  return spec.isMissing({
    profile: partial.profile ?? null,
    contact: partial.contact ?? null,
    references: partial.references ?? [],
  });
}

/** Loads the snapshot the manifest reasons over for a given profile id. */
export async function loadProfileSnapshot(
  supabase: SupabaseClient,
  profileId: string,
): Promise<ProfileSnapshot> {
  const [{ data: profile }, { data: contact }, { data: references }] =
    await Promise.all([
      supabase.from("profiles").select("*").eq("id", profileId).maybeSingle(),
      supabase
        .from("contact_info")
        .select("*")
        .eq("profile_id", profileId)
        .maybeSingle(),
      supabase
        .from("profile_references")
        .select("name, relationship, phone, email")
        .eq("profile_id", profileId),
    ]);

  return {
    profile: profile ?? null,
    contact: contact ?? null,
    references: (references as ProfileReference[] | null) ?? [],
  };
}

/** Returns the manifest entries the member is currently missing. */
export function getMissingRequiredFields(
  snap: ProfileSnapshot,
): RequiredFieldSpec[] {
  return REQUIRED_FIELDS.filter((f) => f.isMissing(snap));
}

/** Convenience: load snapshot then compute missing fields. */
export async function getMissingRequiredFieldsForProfile(
  supabase: SupabaseClient,
  profileId: string,
): Promise<RequiredFieldSpec[]> {
  const snap = await loadProfileSnapshot(supabase, profileId);
  return getMissingRequiredFields(snap);
}
